// #tag: WEB AUDIO SOUND FX ENGINE
// #purpose: Synthesizes and plays UI audio cues (clicks, notifications, swooshes, success triggers) with volume control and accessibility mute checks.
export type UiSoundEvent =
  | 'click'
  | 'search'
  | 'resultFound'
  | 'notificationBell'
  | 'download'
  | 'error'
  | 'warning'
  | 'success'
  | 'swoosh'
  | 'uploadComplete'
  | 'uploadStart'
  | 'notificationPop'
  | 'toggle';

export interface UiSoundConfig {
  enabled: boolean;
  volume: number;
  respectReducedMotion: boolean;
  respectSystemSilent: boolean;
  animations: boolean;
}

const SOUND_URLS: Record<UiSoundEvent, string> = {
  click: '/assets/audio/ui/click.wav',
  toggle: '/assets/audio/ui/toggle.wav',
  search: '/assets/audio/ui/search.wav',
  resultFound: '/assets/audio/ui/success.wav',
  success: '/assets/audio/ui/success.wav',
  error: '/assets/audio/ui/error.wav',
  warning: '/assets/audio/ui/warning.wav',
  notificationBell: '/assets/audio/ui/notification.wav',
  download: '/assets/audio/ui/download.wav',
  notificationPop: '/assets/audio/ui/notification-pop.wav',
  swoosh: '/assets/audio/ui/swoosh.wav',
  uploadStart: '/assets/audio/upload/upload-start.wav',
  uploadComplete: '/assets/audio/upload/upload-complete.wav',
};

const DEFAULT_CONFIG: UiSoundConfig = {
  enabled: false,
  volume: 0.4,
  respectReducedMotion: true,
  respectSystemSilent: true,
  animations: true,
};

let config: UiSoundConfig = { ...DEFAULT_CONFIG };
let unlocked = false;
const cache = new Map<string, HTMLAudioElement>();

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function shouldPlay(): boolean {
  if (!config.enabled) return false;
  if (!config.animations) return false;
  if (config.respectReducedMotion && prefersReducedMotion()) return false;
  if (config.respectSystemSilent && document.hidden) return false;
  return true;
}

function getAudio(url: string): HTMLAudioElement {
  let audio = cache.get(url);
  if (!audio) {
    audio = new Audio(url);
    audio.preload = 'auto';
    cache.set(url, audio);
  }
  return audio;
}

export function setUiSoundConfig(patch: Partial<UiSoundConfig>) {
  config = { ...config, ...patch };
  document.documentElement.classList.toggle('no-sound', !config.enabled);
}

// #tag: AUDIO AUTOPLAY UNLOCKER
// #purpose: Primes audio contexts on initial user gesture to comply with browser autoplay security constraints.
export function unlockUiSound() {
  if (unlocked) return;
  unlocked = true;
  Object.values(SOUND_URLS).forEach((url) => {
    const audio = getAudio(url);
    audio.volume = 0;
    const playPromise = audio.play();
    if (playPromise) {
      playPromise
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = config.volume;
        })
        .catch(() => {
          /* autoplay policy — will unlock on next gesture */
          unlocked = false;
        });
    }
  });
}

// #tag: UI SOUND DISPATCHER
// #purpose: Instantiates and plays sound effect clones matching requested action events.
export function playUiSound(event: UiSoundEvent, options?: { preview?: boolean }) {
  if (!options?.preview && !shouldPlay()) return;

  if (!unlocked) unlockUiSound();

  const url = SOUND_URLS[event];
  const template = getAudio(url);
  const audio = template.cloneNode() as HTMLAudioElement;
  const volume = options?.preview
    ? Math.max(0, Math.min(1, config.volume))
    : Math.max(0, Math.min(1, config.volume));
  audio.volume = volume;

  void audio.play().catch(() => {
    /* ignore playback failures */
  });
}

export function preloadUiSounds() {
  Object.values(SOUND_URLS).forEach((url) => {
    getAudio(url).load();
  });
}
